import type { TimelineAnnotationAuthoringSource, TimelineAnnotationKind, TimelineMarker } from "@/state/timeline/timeline.types";

import type { AnnotationMarkerPalette, AnnotationMarkerReadModel } from "./annotationMarker.types";

const FRESH_MARKER_MS = 4200;

const PALETTES: Record<TimelineAnnotationKind, AnnotationMarkerPalette> = {
  creative: {
    tone: "creative",
    border: "rgba(244, 114, 182, 0.88)",
    fill: "rgba(131, 24, 67, 0.72)",
    glow: "rgba(244, 114, 182, 0.44)",
    text: "rgba(255, 241, 242, 0.98)",
    badgeBg: "rgba(159, 18, 57, 0.88)",
  },
  continuity: {
    tone: "continuity",
    border: "rgba(250, 204, 21, 0.9)",
    fill: "rgba(113, 63, 18, 0.74)",
    glow: "rgba(250, 204, 21, 0.42)",
    text: "rgba(255, 251, 235, 0.98)",
    badgeBg: "rgba(146, 64, 14, 0.88)",
  },
  timing: {
    tone: "timing",
    border: "rgba(96, 165, 250, 0.9)",
    fill: "rgba(30, 58, 138, 0.74)",
    glow: "rgba(96, 165, 250, 0.42)",
    text: "rgba(239, 246, 255, 0.98)",
    badgeBg: "rgba(29, 78, 216, 0.88)",
  },
  audio: {
    tone: "audio",
    border: "rgba(52, 211, 153, 0.9)",
    fill: "rgba(6, 95, 70, 0.76)",
    glow: "rgba(52, 211, 153, 0.42)",
    text: "rgba(236, 253, 245, 0.98)",
    badgeBg: "rgba(6, 95, 70, 0.9)",
  },
  npc: {
    tone: "npc",
    border: "rgba(248, 113, 113, 0.92)",
    fill: "rgba(127, 29, 29, 0.78)",
    glow: "rgba(248, 113, 113, 0.40)",
    text: "rgba(254, 242, 242, 0.98)",
    badgeBg: "rgba(185, 28, 28, 0.9)",
  },
  intent: {
    tone: "intent",
    border: "rgba(167, 139, 250, 0.92)",
    fill: "rgba(76, 29, 149, 0.76)",
    glow: "rgba(167, 139, 250, 0.42)",
    text: "rgba(245, 243, 255, 0.98)",
    badgeBg: "rgba(91, 33, 182, 0.9)",
  },
  publish: {
    tone: "publish",
    border: "rgba(251, 146, 60, 0.92)",
    fill: "rgba(154, 52, 18, 0.78)",
    glow: "rgba(251, 146, 60, 0.44)",
    text: "rgba(255, 247, 237, 0.98)",
    badgeBg: "rgba(194, 65, 12, 0.9)",
  },
};

function shortLabelForKind(kind: TimelineAnnotationKind) {
  switch (kind) {
    case "continuity":
      return "CONT";
    case "timing":
      return "TIME";
    case "audio":
      return "AUD";
    case "npc":
      return "NPC";
    case "intent":
      return "INT";
    case "publish":
      return "PUB";
    default:
      return "CRTV";
  }
}

function titleForKind(kind: TimelineAnnotationKind) {
  switch (kind) {
    case "continuity":
      return "Continuity Note";
    case "timing":
      return "Timing Note";
    case "audio":
      return "Audio Note";
    case "npc":
      return "NPC Note";
    case "intent":
      return "Intent Note";
    case "publish":
      return "Publish Note";
    default:
      return "Creative Note";
  }
}

function compactLabel(label: string) {
  const safe = label.trim();
  if (!safe) return "Annotation marker";
  return safe.length > 42 ? `${safe.slice(0, 39).trimEnd()}...` : safe;
}

function titleCase(value: string | null | undefined) {
  const safe = String(value ?? "").trim();
  if (!safe) return null;
  return safe
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function sourceBadgeForMarker(source: TimelineMarker["annotation"]): TimelineAnnotationAuthoringSource | "voice" | "manual" | "ai" | null {
  const provenance = source?.provenance;
  return provenance?.authoringSource ?? provenance?.captureSource ?? source?.source ?? null;
}

function sourceBadgeLabel(value: ReturnType<typeof sourceBadgeForMarker>): string | null {
  switch (value) {
    case "ai_assisted":
      return "AI assisted";
    case "dictated":
      return "Dictated";
    case "imported":
      return "Imported";
    case "human":
      return "Human";
    case "voice":
      return "Voice";
    case "ai":
      return "AI";
    case "manual":
      return "Manual";
    default:
      return null;
  }
}

export function buildAnnotationMarkerReadModel(args: {
  marker: TimelineMarker;
  pxPerSec: number;
  viewportStartSec: number;
  activeMarkerId?: string | null;
  nowMs?: number;
}): AnnotationMarkerReadModel | null {
  const { marker, pxPerSec, viewportStartSec, activeMarkerId, nowMs = Date.now() } = args;
  const meta = marker.annotation;
  if (!meta || meta.origin !== "annotation_surface") return null;

  const annotationType = meta.type ?? "creative";
  const projectionSource = sourceBadgeForMarker(meta);
  const subtype = (meta.subtype ?? null) as AnnotationMarkerReadModel["subtype"];
  const severity = (meta.severity ?? null) as AnnotationMarkerReadModel["severity"];
  const rawNote = String(meta.rawNote ?? marker.label ?? "").trim();
  const summary = String(meta.summary ?? rawNote).trim() || rawNote || null;
  const nextActionLabel = String(meta.nextAction ?? "").trim() || null;
  const subtypeLabel = titleCase(subtype);
  const tickerText = [severity ? severity.toUpperCase() : null, subtypeLabel, summary]
    .filter(Boolean)
    .join("  •  ");
  const fullLabel = compactLabel(rawNote || String(marker.label ?? ""));
  const isSelected = String(activeMarkerId ?? "") === String(marker.id ?? "");
  const createdAt = Number.isFinite(meta.createdAt) ? Number(meta.createdAt) : 0;
  const shouldTicker = tickerText.length > 34;
  const projection = {
    annotationId: meta.annotationId ?? String(marker.id ?? ""),
    timeSec: Number.isFinite(marker.timeSec) ? Number(marker.timeSec) : null,
    kind: annotationType,
    markerVisible: meta.markerVisible !== false,
    label: summary,
    hasNextAction: !!nextActionLabel,
    sourceBadge: projectionSource,
  };

  if (!projection.markerVisible) return null;

  return {
    markerId: String(marker.id ?? ""),
    timeSec: Number(marker.timeSec ?? 0),
    projection,
    shortLabel: shortLabelForKind(annotationType),
    fullLabel,
    hoverTitle: titleForKind(annotationType),
    annotationType,
    subtype,
    severity,
    summary,
    annotationSource: meta.source ?? "manual",
    sourceBadgeLabel: sourceBadgeLabel(projectionSource),
    nextActionLabel,
    provenance: meta.provenance ?? null,
    visualState: meta.state ?? "marker_only",
    palette: PALETTES[annotationType] ?? PALETTES.creative,
    assetLabel: String(meta.assetLabel ?? "active clip").trim() || "active clip",
    clipId: String(meta.clipId ?? "").trim() || null,
    linkedAudio: !!meta.linkedAudio,
    frameIndex: Number.isFinite(meta.frameIndex) ? Number(meta.frameIndex) : null,
    anchorTimecode: String(meta.anchorTimecode ?? "").trim() || null,
    rawNote: rawNote || fullLabel,
    createdAt,
    isSelected,
    isFresh: createdAt > 0 && nowMs - createdAt < FRESH_MARKER_MS,
    showBadge: pxPerSec >= 56,
    showLabel: pxPerSec >= 96,
    shouldTicker,
    tickerText: tickerText || null,
    xPx: (Number(marker.timeSec ?? 0) - viewportStartSec) * pxPerSec,
    marker,
  };
}